<template>
  <!-- Mirrored by EventSkeleton -->
  <div class="event-row" data-testid="event-row"
       :class="[getClass, { clickable: isClickable }]"
       @click="redirectToAthlete"
  >
    <div class="event-row__time">
      <span class="event-row__start">{{startLabel}}</span>
      <span class="event-row__end">{{endLabel}}</span>
    </div>

    <div class="event-row__body">
      <div v-if="!isHome" class="event-row__date">{{ formattedDate }}</div>
      <div v-if="isHome" class="event-row__title">{{event.title}}</div>
      <p v-if="event.notes" class="event-row__notes"
         :class="{ 'event-row__notes--expanded': notesExpanded, 'event-row__notes--clickable': notesAreLong && !isHome }"
         @click="onNotesClick"
      >{{ displayedNotes }}</p>
      <button v-if="notesAreLong" type="button"
              class="event-row__notes-toggle"
              @click.stop="notesExpanded = !notesExpanded"
      >{{ notesExpanded ? $t('calendar.notesLess') : $t('calendar.notesMore') }}</button>
    </div>

    <span v-if="event.isCancelled || event.user_response_status === false" class="event-row__cancelled"><i class="fe-x"/>{{ $t('calendar.cancelled') }}</span>
    <span v-else-if="event.user_response_status" class="event-row__approved"><i class="fe-check"/>{{ $t('calendar.approved') }}</span>

    <span v-if="categoryName" class="event-row__chip" :class="chipClass" :style="chipTextStyle">
      <span class="event-row__chip-dot" :style="chipStyle"/>{{ categoryName }}
    </span>

    <slot name="append" />
  </div>
</template>

<style lang="scss">
.event-row{
  position:relative;
  display:flex;
  align-items:center;
  gap:0.9em;
  overflow:hidden;
  background:#fff;
  border:1px solid $color_border;
  border-radius:12px;
  padding:0.75em 0.9em;
  margin-bottom:0.5em;
  font-size:0.9rem;
  transition:border-color 0.15s ease;

  // Hover: border shift + a soft dim.
  &.clickable{
    cursor:pointer;

    &::after{
      content:'';
      position:absolute;
      inset:0;
      background:rgba(30, 41, 90, 0.05);
      opacity:0;
      transition:opacity 0.15s ease;
      pointer-events:none;
    }

    &:hover{
      border-color:rgba($color_primary, 0.45);

      &::after{
        opacity:1;
      }
    }
  }

  &.cancelled{
    background:rgba($event_cancelled_primary, 0.04);
    border-color:rgba($event_cancelled_primary, 0.35);

    &.clickable:hover{
      border-color:rgba($event_cancelled_primary, 0.55);
    }

    .event-row__title,
    .event-row__date{
      text-decoration:line-through;
      color:$color_primary_muted;
    }
  }

  &__time{
    display:flex;
    flex-direction:column;
    min-width:3.2em;
    flex-shrink:0;
  }

  &__start{
    font-weight:700;
    font-size:0.82em;
    color:$color_text;
    line-height:1.3;
    font-variant-numeric:tabular-nums;
  }

  &__end{
    font-size:0.72em;
    color:$color_primary_muted;
    font-variant-numeric:tabular-nums;
  }

  &__body{
    flex:1;
    min-width:0;
  }

  // In the non-home variant the date is the row's lead — title typography.
  &__date{
    font-size:0.95em;
    font-weight:600;
    color:$color_text;
    line-height:1.35;
  }

  &__title{
    font-size:0.95em;
    font-weight:600;
    color:$color_text;
    line-height:1.35;
    white-space:nowrap;
    overflow:hidden;
    text-overflow:ellipsis;
  }

  &__notes{
    font-size:0.78em;
    color:$color_primary_muted;
    margin:0.15em 0 0;
    word-break:break-word;

    // The collapsed/expanded split is done in JS (displayedNotes) — this only
    // restores the author's line breaks once the full text is shown.
    &--expanded{
      white-space:pre-line;
    }

    &--clickable{
      cursor:pointer;
    }
  }

  &__notes-toggle{
    padding:0;
    border:0;
    background:none;
    font-size:0.72em;
    font-weight:600;
    color:$color_primary;
    cursor:pointer;

    &:hover{
      text-decoration:underline;
    }
  }

  // Status pills, deliberately outside the category color language.
  &__cancelled,
  &__approved{
    display:inline-flex;
    align-items:center;
    gap:0.4em;
    margin-left:auto;
    flex-shrink:0;
    padding:0.25em 0.75em;
    border-radius:20px;
    font-size:0.68em;
    font-weight:600;
    white-space:nowrap;
  }

  &__cancelled{
    background:rgba($event_cancelled_primary, 0.07);
    border:1px solid rgba($event_cancelled_primary, 0.4);
    color:darken($event_cancelled_primary, 12%);
  }

  &__approved{
    background:rgba($event_approved_primary, 0.07);
    border:1px solid rgba($event_approved_primary, 0.4);
    color:darken($event_approved_primary, 12%);
  }

  &__cancelled + &__chip,
  &__approved + &__chip{
    margin-left:0;
  }

  // Outlined badge: solid dot + bold text in the event's true color.
  &__chip{
    display:inline-flex;
    align-items:center;
    gap:0.45em;
    margin-left:auto;
    flex-shrink:0;
    max-width:40%;
    padding:0.25em 0.75em;
    background:#fff;
    border:1px solid $color_border;
    border-radius:20px;
    font-size:0.68em;
    font-weight:500;
    color:$color_primary_muted;
    white-space:nowrap;
    overflow:hidden;
    text-overflow:ellipsis;

    // Same colors the calendar paints uncategorized events with.
    &--appointment{
      color:$color_primary;
      .event-row__chip-dot{ background:$color_primary; }
    }

    &--general{
      color:#155724;
      .event-row__chip-dot{ background:#c3e6cb; }
    }
  }

  &__chip-dot{
    width:8px;
    height:8px;
    border-radius:50%;
    flex-shrink:0;
  }
}
</style>

<script>
import {getReadableTextColor} from "@utils/util";
import { toUserZone, formatEventTime } from "../utils/timezone";
import { useEventCategories } from "@src/queries/calendar";

// Characters of a note's first line shown before the «more» toggle takes over.
const NOTES_PREVIEW_LIMIT = 90;

export default{
  props:{
    event: Object,
    isHome:{
      type: Boolean,
      default: false
    }
  },

  setup(){
    return { categories: useEventCategories().categories };
  },

  data(){
    return {
      notesExpanded: false,
    };
  },

  computed:{
    // Localized human date for the history/profile variant, e.g. "Παρ, 17 Ιουλ 2026".
    formattedDate(){
      if (!this.event?.start) return '';
      return toUserZone(this.event.start)
        .setLocale(this.$i18n.locale || 'el')
        .toFormat('ccc, d MMM yyyy');
    },
    startLabel(){
      return formatEventTime(this.event.start);
    },
    endLabel(){
      return formatEventTime(this.event.end);
    },
    getClass(){
      if(this.event.isCancelled || this.event.user_response_status === false) return 'cancelled';
      if(this.event.user_response_status) return 'approved'
      if(this.event.type === 0) return 'notes';
      return '';
    },
    category(){
      if (!this.event?.categoryId) return null;
      return this.categories.find(c => c.id === this.event.categoryId) || null;
    },
    categoryColor(){
      return this.category?.color || null;
    },
    isClickable(){
      return this.isHome && !!this.event.athleteId;
    },
    categoryName(){
      if (this.category?.name) return this.category.name;
      // Uncategorized events fall back to their kind.
      return this.event.type === 0
        ? this.$t('calendar.categories.typeGeneral')
        : this.$t('calendar.categories.typeAppointment');
    },
    // The badge wears the event's true color: dot gets the raw category
    // color, text a readable shade of it; type defaults via class modifiers.
    chipStyle(){
      if (!this.category) return null;
      return { backgroundColor: this.categoryColor };
    },
    chipTextStyle(){
      if (!this.category) return null;
      return { color: getReadableTextColor(this.categoryColor) };
    },
    chipClass(){
      if (this.category) return null;
      return this.event.type === 0
        ? 'event-row__chip--general'
        : 'event-row__chip--appointment';
    },
    // Long enough to be worth collapsing, or carrying its own line breaks.
    notesAreLong(){
      const notes = this.event.notes || '';
      return notes.length > NOTES_PREVIEW_LIMIT || notes.includes('\n');
    },
    // Collapsed shows a deterministic preview — the first line, capped — with an
    // ellipsis flagging there's more. No DOM measurement, no CSS clamp.
    displayedNotes(){
      const notes = this.event.notes || '';
      if (this.notesExpanded || !this.notesAreLong) return notes;
      const firstLine = notes.split('\n')[0];
      if (firstLine.length > NOTES_PREVIEW_LIMIT) {
        return firstLine.slice(0, NOTES_PREVIEW_LIMIT).trimEnd() + '…';
      }
      // Shorter first line but more content follows (extra lines).
      return firstLine + '…';
    },
  },

  methods:{
    onNotesClick(e){
      // On home the whole row navigates — let the click bubble through. Elsewhere
      // (e.g. the history modal) a long note toggles inline instead.
      if(this.isHome || !this.notesAreLong) return;
      e.stopPropagation();
      this.notesExpanded = !this.notesExpanded;
    },
    redirectToAthlete(){
      if(this.event.athleteId && this.isHome) this.$router.push({name: 'view-athlete', params: {athleteId: this.event.athleteId}})
    }
  }
}
</script>
